import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CgProfile } from 'react-icons/cg';
import { toast } from 'react-toastify';
import CompanyForm, {
  CompanyFormFields,
} from '../../components/Company/CompanyForm';
import { useAuth } from '../../contexts/auth';
import PersonType from '../../enums/PersonType';
import api from '../../services/api';
import {
  PersonAddresses,
  PersonAdressFormFields,
  PersonAdressModel,
} from '../../components/Person/PersonAddresses';
import SignaturePad from '../../components/Signature/SignaturePad';
import CustomButton from '../../components/CustomButton/CustomButton';
import selectFile from '../../services/fileService';
import Card from '../../components/Card/Card';
import CompanyEmailForm, {
  CompanyMailFormFields,
} from '../../components/Company/CompanyMailForm';

export default function CompaniesEdit() {
  const {
    user: { organization },
  } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [formFields, setFormFields] = useState<CompanyFormFields>(
    null as unknown as CompanyFormFields
  );
  const [companyMailFormFields, setCompanyMailFormFields] =
    useState<CompanyMailFormFields>(null as unknown as CompanyMailFormFields);
  const [formErrors, setFormErrors] = useState({});

  const [addressUpdateIsLoading, setAddressUpdateIsLoading] = useState(false);
  const [logo, setLogo] = useState('');
  const [isLogoLoading, setIsLogoLoading] = useState(false);
  const [isAddressesLoading, setIsAddressesLoading] = useState(true);
  const [addresses, setAddresses] = useState<PersonAdressModel[]>([]);
  const [formAddressErrors, setAddressErrors] = useState({});
  const [addressFormFields, setAddressFormFields] =
    useState<PersonAdressFormFields>(null as unknown as PersonAdressFormFields);
  const [signature, setSignature] = useState('');

  async function getCompany() {
    if (organization && id) {
      const { data } = await api.get(
        `organizations/${organization.id}/companies/${id}`
      );

      setFormFields({
        type: data.type,
        identifier: data.identifier,
        name: data.name,
        alias: data.alias,
        useAlias: data.useAlias,
        email: data.email,
        phone: data.phone,
      });

      setCompanyMailFormFields({
        emailBody: data.emailBody,
        emailSignature: data.emailSignature,
        emailSubject: data.emailSubject,
      });

      setSignature(data.signature);
      setIsLoading(false);
    }
  }

  async function getAddresses() {
    if (organization) {
      setIsAddressesLoading(true);
      const response = await api.get(
        `organizations/${organization.id}/companies/${id}/addresses`
      );
      setAddresses(response.data.data);
      setIsAddressesLoading(false);
    }
  }

  const onSubmit = async (data: CompanyFormFields) => {
    try {
      await api.put(`organizations/${organization.id}/companies/${id}`, {
        alias:
          Number(data.type) === PersonType.Juridical ? data.alias : undefined,
        ...data,
      });

      navigate('/companies');
    } catch (e: any) {
      if (e.response.data.errors) {
        setFormErrors(e.response.data.errors);
      }
    }
  };

  const setAddressFields = (data: any) => {
    setAddressFormFields({
      id: data.id,
      address: data.address,
      number: data.number,
      complement: data.complement,
      neighborhood: data.neighborhood,
      city: data.city,
      state: data.state,
      postalCode: data.postalCode,
    });
  };

  const onSubmitAddress = async (data: PersonAdressFormFields) => {
    try {
      const { id: addressId, ...address } = data;
      let response;
      if (data.id) {
        response = await api.put(
          `organizations/${organization.id}/companies/${id}/addresses/${addressId}`,
          address
        );
      } else {
        response = await api.post(
          `organizations/${organization.id}/companies/${id}/addresses`,
          address
        );
      }

      setAddressFields(response.data);
      toast.success('Endereço salvo');
      getAddresses();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setAddressErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  const editAddress = async (addressId: number) => {
    try {
      setAddressUpdateIsLoading(true);
      const { data } = await api.get(
        `organizations/${organization.id}/companies/${id}/addresses/${addressId}`
      );
      setAddressFields(data);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.errors.reduce(
          (acc: string, error: string) => acc + error,
          ''
        )
      );
    } finally {
      setAddressUpdateIsLoading(false);
    }
  };

  const removeAddress = useCallback(
    async (address: PersonAdressModel) => {
      try {
        const displayName = `${address.address}, ${address.number}`;
        if (
          // eslint-disable-next-line no-restricted-globals
          confirm(
            `Você tem certeza que deseja remover o endereço "${displayName}"? Essa ação não poderá ser desfeita.`
          )
        ) {
          await api.delete(
            `organizations/${organization.id}/companies/${id}/addresses/${address.id}`
          );
          getAddresses();
          toast.success(`O endereço "${displayName}" foi removido da Empresa`);
        }
      } catch (e) {
        // TODO: Recarregar lista
        toast.error(
          'Houve um problema ao tentar remover o endereço da Empresa. Tente novamente.'
        );
      }
    },
    [organization.id, id]
  );

  const handleSignature = async (dataUrl: string | undefined) => {
    try {
      const { data } = await api.post(
        `organizations/${organization.id}/companies/${id}/sign`,
        {
          signature: dataUrl,
        }
      );
      setSignature(data.signature);
      toast.success('Assinatura salva');
    } catch (e: any) {
      console.log(e);
    }
  };

  const getLogo = async () => {
    try {
      setIsLogoLoading(true);
      const { data } = await api.get(
        `organizations/${organization.id}/companies/${id}/logo`
      );
      setLogo(data);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        toast.info('O Logotipo ainda não foi cadastrado');
      }
    } finally {
      setIsLogoLoading(false);
    }
  };

  const addFile = async () => {
    try {
      const files = await selectFile(['image/png', 'image/jpeg'], false);
      if (files && files?.length > 0) {
        toast.info('Aguarde o upload do Logotipo');
        await api.postForm(
          `organizations/${organization.id}/companies/${id}/logo`,
          { logo: files[0] }
        );
        toast.success('Logotipo salvo com sucesso');
        getLogo();
      }
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  const removeFile = async () => {
    try {
      if (
        // eslint-disable-next-line no-restricted-globals
        confirm(
          'Você deseja remover o logotipo da Empresa? Essa ação não poderá ser desfeita'
        )
      ) {
        await api.delete(
          `organizations/${organization.id}/companies/${id}/logo`
        );

        toast.success('Logotipo removida com sucesso');
        setLogo('');
      }
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  const setLogoTab = () => {
    setActiveTab(1);
    if (!logo) {
      getLogo();
    }
  };

  useEffect(() => {
    getCompany();
    getAddresses();
  }, [organization, id]);

  return (
    <>
      <ul className='nav nav-tabs mb-3'>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${activeTab === 0 ? 'active' : ''}`}
            aria-current='page'
            onClick={() => setActiveTab(0)}
          >
            Dados
          </button>
        </li>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => setLogoTab()}
          >
            Logotipo
          </button>
        </li>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${activeTab === 2 ? 'active' : ''}`}
            onClick={() => setActiveTab(2)}
          >
            Endereços
          </button>
        </li>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${activeTab === 3 ? 'active' : ''}`}
            onClick={() => setActiveTab(3)}
          >
            Assinatura
          </button>
        </li>
        <li className='nav-item'>
          <button
            type='button'
            className={`nav-link ${activeTab === 4 ? 'active' : ''}`}
            onClick={() => setActiveTab(4)}
          >
            E-mail
          </button>
        </li>
      </ul>
      {activeTab === 0 && (
        <CompanyForm
          isUpdate
          isLoading={isLoading}
          title={`Editar Empresa ${id}`}
          formFields={formFields}
          formErrors={formErrors}
          onSubmit={onSubmit}
        />
      )}

      {activeTab === 1 && (
        <Card header='Logotipo da empresa' isLoading={isLogoLoading}>
          {logo && !isLogoLoading && (
            <img width={300} src={logo} alt='logotipo' />
          )}
          <div className='mt-3 d-flex gap-2'>
            <CustomButton
              type='button'
              className='btn btn-primary'
              handleClick={() => addFile()}
            >
              Adicionar <CgProfile />
            </CustomButton>

            <CustomButton
              className='btn btn-danger'
              type='button'
              handleClick={() => removeFile()}
            >
              Remover
            </CustomButton>
          </div>
        </Card>
      )}

      {activeTab === 2 && (
        <PersonAddresses
          itens={addresses}
          onSubmitAddress={onSubmitAddress}
          removeAddress={removeAddress}
          editAddress={editAddress}
          formErrors={formAddressErrors}
          formFields={addressFormFields}
          isUpdateLoading={addressUpdateIsLoading}
        />
      )}

      {activeTab === 3 && (
        <div className='mb-3'>
          <SignaturePad
            handleSignature={handleSignature}
            signature={signature}
            height={600}
          />
        </div>
      )}

      {activeTab === 4 && (
        <div>
          <CompanyEmailForm
            id={Number(id)}
            isLoading={isLoading}
            formFields={companyMailFormFields}
            setFormFields={setCompanyMailFormFields}
          />
        </div>
      )}
    </>
  );
}
